import { type FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import {
  useLazyGetContractsQuery,
  useUpgradeProductOfDayMutation,
} from '@/api/queries/contract';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { additionalDiscountForUsdt } from '@/common/config/common';
import { prizesImage } from '@/common/config/prizes-image';
import { useStorePrice } from '@/common/hooks/apple-price';
import { usePaymentStatus } from '@/common/hooks/payment-status';
import { usePurchaseAttempt } from '@/common/hooks/purchase-attempt';
import { useTonAutoConnect } from '@/common/hooks/ton-auto-connect';
import { multiplication } from '@/common/lib/decimal';
import { SendAnalitics } from '@/common/lib/playdeck';
import { type ProductOfDayDataType } from '@/common/types/contract';
import { type TONTransactionRequestDataType } from '@/common/types/query/user';
import { H1, StringSmall } from '@/components/Typography/Typography';
import { GreyBlurButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { MotionPicture, Picture } from '@/components/ui/Picture/Picture';
import TONandStarsBuyButtons from '@/components/ui/TONandStarsBuyButtons/TONandStarsBuyButtons';
import PendingStatus from '@/components/ui/ViewStatusComponents/PendingStatus';
import {
  changePaymentStatus,
  clearPendingPurchase,
  setPendingPurchase,
} from '@/redux/reducers/paymentsReducer';
import { subtractBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import purpleWheelPng from '/img/contract/purplewheel.png';
import purpleWheelWebp from '/img/contract/purplewheel.webp';
import lightPng from '/img/contract/vertical-light.png';
import lightWebp from '/img/contract/vertical-light.webp';
import starPng from '/img/icons/star.png';
import starWebp from '/img/icons/star.webp';

interface BuyUpgradeProductOfDayProps {
  product: ProductOfDayDataType;
}

const BuyUpgradeProductOfDay: FC<BuyUpgradeProductOfDayProps> = ({
  product,
}) => {
  const { t } = useTranslation();
  const paymentStatus = usePaymentStatus();
  const { secondModal, thirdModal } = useContext(ModalContext);
  const [waiting, setWaiting] = useState<boolean>(false);
  const [buyUpgradePlan] = useUpgradeProductOfDayMutation();
  const [trigger] = useLazyGetContractsQuery();
  const connectWalletAndDoTransaction = useTonAutoConnect();
  const dispatch = useAppDispatch();
  const usdPrice = useMemo(
    () =>
      multiplication(
        product.upgradeCost.amountUsd,
        additionalDiscountForUsdt.coefficient,
      ),
    [product],
  );

  usePurchaseAttempt({
    item: 'upgrade product of day',
    item_id: product.slot.toString(),
    price: product.upgradeCost.amountUsd,
    currency: 'usdt',
  });

  const sendAnaliticsPending = (currency: 'ton' | 'xtr'): void => {
    SendAnalitics.purchasePending({
      item: 'upgrade product of day',
      item_id: product.slot.toString(),
      price: product.upgradeCost.amountUsd,
      currency,
    });
    dispatch(
      setPendingPurchase({
        item: 'upgrade product of day',
        item_id: product.slot.toString(),
        price: product.upgradeCost.amountUsd,
        currency,
      }),
    );
  };

  const sendAnaliticsSuccessful = (currency: 'usdt'): void => {
    SendAnalitics.purchaseSuccessfull({
      item: 'upgrade product of day',
      item_id: product.slot.toString(),
      price: product.upgradeCost.amountUsd,
      currency,
    });
  };

  const onBuy = async (currency: 'XTR' | 'TON'): Promise<void> => {
    if (currency === 'TON') {
      await new Promise<void>((resolve, reject) => {
        connectWalletAndDoTransaction(
          async () =>
            (await buyUpgradePlan({
              currency: 'ton',
              slot: product.slot,
            }).unwrap()) as TONTransactionRequestDataType,
          {
            onSuccess: () => {
              resolve();
              sendAnaliticsPending('ton');
            },
            onError: () => {
              ToastNotification(t('Something went wrong, please try again'));
              reject(new Error('Transaction failed'));
              dispatch(clearPendingPurchase());
            },
          },
        );
      });
    } else {
      const response = await buyUpgradePlan({
        currency: 'xtr',
        slot: product.slot,
      }).unwrap();

      if (response && 'link' in response) {
        await new Promise<void>((resolve, reject) => {
          Telegram.WebApp.openInvoice(response.link, (status) => {
            if (status === 'cancelled' || status === 'failed') {
              ToastNotification(t('Something went wrong, please try again'));
              reject(new Error(`Payment ${status}`));
              dispatch(clearPendingPurchase());
            } else {
              resolve();
              sendAnaliticsPending('xtr');
            }
            console.log(`payment status ${status}`);
          });
        });
      } else {
        ToastNotification(t('Something went wrong, please try again'));
      }
    }
  };

  const onClick = (currency: 'XTR' | 'TON'): void => {
    setWaiting(true);
    onBuy(currency)
      .then(() => {
        dispatch(changePaymentStatus('pending'));
        setWaiting(false);
      })
      .catch(() => {
        dispatch(changePaymentStatus(undefined));
        setWaiting(false);
      });
  };

  const handleClose = (): void => {
    dispatch(changePaymentStatus(undefined));

    if (thirdModal.isOpen) {
      thirdModal.closeModal();
    } else {
      secondModal.closeModal();
    }
  };

  const buyUsdt = async (): Promise<void> => {
    try {
      const result = await buyUpgradePlan({
        currency: 'usdt',
        slot: product.slot,
      });

      if (result.error) {
        throw new Error(result.error.toString());
      }

      dispatch(
        subtractBalance({
          type: 'usdt',
          value: usdPrice,
        }),
      );
      sendAnaliticsSuccessful('usdt');
      handleClose();
      trigger();
    } catch (error) {
      console.error(error);
      ToastNotification(t('Something went wrong, please try again'));
      dispatch(changePaymentStatus(undefined));
    }
  };

  useEffect(() => {
    if (paymentStatus === 'success') {
      handleClose();
    }
  }, [paymentStatus]);

  const storePrice = useStorePrice(product.upgradeCost.amountUsd);

  return (
    <div className="flex flex-col items-center gap-4 pb-[3rem] h-full pt-[calc(var(--header-height)_+_var(--tg-safe-area-inset-top)_+_2.5rem)]">
      {paymentStatus === 'pending' ? (
        <PendingStatus />
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <H1 weight="extrabold">{t('Upgrade Reward')}</H1>
            <StringSmall className="!text-white/80 text-center">
              {t('Unlock better spin rewards')}
            </StringSmall>
          </div>
          <div className="flex flex-col flex-1 gap-[10px] items-center justify-center">
            <div className="relative flex">
              <MotionPicture
                initial={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 aspect-square w-[220px]"
                img={lightPng}
                webp={lightWebp}
                alt="light"
              />
              <div
                style={{ width: 90, left: 34, bottom: 13 }}
                className="absolute aspect-[90/155] w-[90px] flex items-center justify-center"
              >
                <span className="absolute w-full h-full rounded-[50%] bg-irrigo-purple blur-[56px]"></span>
                <span className="absolute aspect-[69/118] w-[76.66%] rounded-[50%] bg-irrigo-purple blur-[18px]"></span>
              </div>
              <MotionPicture
                img={prizesImage.png}
                webp={prizesImage.webp}
                style={{ width: 209, opacity: 1 }}
                className="absolute aspect-square left-[23px] bottom-[9px] w-[164px]"
                alt="stars"
              />
              <MotionPicture
                style={{
                  width: 164,
                  height: 164,
                  filter: 'drop-shadow(16px -16px 24px rgba(24, 26, 32, 0.50))',
                }}
                img={purpleWheelPng}
                webp={purpleWheelWebp}
                alt="elite box"
              />
            </div>
            <div
              className={cn(
                'flex gap-2 px-3 py-1 rounded-full bg-white/10 z-10 relative',
              )}
            >
              <div className="flex items-center">
                <span className="text-[1.5rem] font-extrabold text-gold leading-none">
                  +{product.rewardInfo.premium.extraStarPoints}
                </span>
                <Picture
                  className="size-6"
                  img={starPng}
                  webp={starWebp}
                  alt="star"
                />
              </div>
            </div>
          </div>
          {/* <div className="flex flex-col flex-1 relative items-center">
            <UpgradeGreenArrowsAnimation className="absolute left-1/2 -translate-x-1/2 top-12 z-[-2] w-[6.5rem] !-space-y-4" />
            <div className="bg-white/90 size-20 rounded-full blur-[32px] absolute left-1/2 -translate-x-1/2 top-[5.375rem] z-[-1]" />
            <Picture
              className="size-[11.5rem] mt-[3.25rem]"
              alt="product"
              img={image?.png}
              webp={image?.webp}
            />
            <div className="bg-white/10 rounded-full h-8 gap-0.5 px-3 flex items-center">
              <H1 weight="extrabold" className="!text-gold">
                +{product.rewardInfo.premium.extraStarPoints}
              </H1>
              <Picture
                className="size-6"
                img={starPng}
                webp={starWebp}
                alt="star"
              />
            </div>
          </div> */}
          <H1 weight="extrabold">
            {storePrice ?? `$ ${product.upgradeCost.amountUsd}`}
          </H1>

          <div className="flex flex-col self-stretch">
            <TONandStarsBuyButtons
              onClick={onClick}
              onUsdtBuy={buyUsdt}
              isLoading={waiting}
              usdPrice={usdPrice}
              price={product.upgradeCost.amountUsd}
              starPrice={product.upgradeCost.amountStars}
              discount={additionalDiscountForUsdt.percentage}
              mustBeUsdtButton={true}
              variant="switch"
            />
            <GreyBlurButton
              onClick={handleClose}
              className="mx-6 !w-auto mt-3"
              text={t('Close')}
            />
          </div>
        </>
      )}
      {paymentStatus === 'pending' && (
        <Close
          onClick={handleClose}
          imageVariant="modal"
          size="lg"
          className="absolute bottom-[calc(var(--tg-safe-area-inset-bottom,0px)+30px)]"
        />
      )}
    </div>
  );
};

export default BuyUpgradeProductOfDay;
